/**
 * Sprint 17e: Mission refresh configuration.
 * Controls TTL, refresh intervals, and budget for the tiered refresh system.
 */

export interface RefreshConfig {
  categoryTTL: Record<string, number>;
  agentRefreshInterval: Record<string, number>;
  dailyAgentBudget: number;
  pipelineRefreshGuardMs: number;
  minBackgroundDurationMs: number;
}

export const HOUR = 60 * 60 * 1000;
export const DAY = 24 * HOUR;

export const DEFAULT_REFRESH_CONFIG: RefreshConfig = {
  categoryTTL: {
    shopping: 21 * DAY,
    food: 15 * DAY,
    travel: 42 * DAY,
    entertainment: 9 * DAY,
    content: 30 * DAY,
    health: 42 * DAY,
    savings: 21 * DAY,
    consumables: 21 * DAY,
    people: 90 * DAY,
  },
  agentRefreshInterval: {
    shopping: 12 * HOUR,
    food: 24 * HOUR,
    travel: 24 * HOUR,
    entertainment: 12 * HOUR,
    content: 24 * HOUR,
    health: 48 * HOUR,
    savings: 24 * HOUR,
    consumables: 24 * HOUR,
    people: 72 * HOUR,
  },
  dailyAgentBudget: 4,
  pipelineRefreshGuardMs: 4 * HOUR,
  minBackgroundDurationMs: 5 * 60 * 1000,
};

/** Human-readable category labels */
export const CATEGORY_LABELS: Record<string, string> = {
  shopping: 'Shopping',
  food: 'Food & Dining',
  travel: 'Travel',
  entertainment: 'Entertainment',
  content: 'Content',
  health: 'Health',
  savings: 'Savings',
  consumables: 'Consumables',
  people: 'People',
};

/** Preset TTL options in days */
export const TTL_OPTIONS = [
  { label: '3 days', value: 3 * DAY },
  { label: '7 days', value: 7 * DAY },
  { label: '14 days', value: 14 * DAY },
  { label: '21 days', value: 21 * DAY },
  { label: '30 days', value: 30 * DAY },
  { label: '60 days', value: 60 * DAY },
  { label: '90 days', value: 90 * DAY },
];

const STORAGE_KEY = 'ownyou_category_ttl';

/**
 * Get the effective refresh config, merging user overrides from localStorage.
 * Safe to call from web workers (localStorage is accessible).
 */
export function getEffectiveRefreshConfig(): RefreshConfig {
  const config = { ...DEFAULT_REFRESH_CONFIG, categoryTTL: { ...DEFAULT_REFRESH_CONFIG.categoryTTL } };
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const overrides = JSON.parse(saved) as Record<string, number>;
      for (const [category, ttl] of Object.entries(overrides)) {
        if (typeof ttl === 'number' && ttl > 0 && category in config.categoryTTL) {
          config.categoryTTL[category] = ttl;
        }
      }
    }
  } catch { /* ignore parse errors */ }
  return config;
}

/**
 * Save user TTL overrides to localStorage.
 */
export function saveCategoryTTLOverrides(overrides: Record<string, number>): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides));
}
